import * as xlsx from 'xlsx';
import FileSaver from 'file-saver';
import pdfmake from 'pdfmake';
// eslint-disable-next-line import/no-unresolved
import pdfFontsSource from 'pdfmake/build/vfs_fonts.js?raw';

import { camelCase } from 'lodash';

import { ExportFileType } from './constants';
import { DataType, TableHeaderKey } from './types';
import { formatValue, generateReportViewRowData, pdfConfig, wordWrap } from './utils';

import { ITableField, ITableInfo, ReportViewType } from 'types';
import { getNestedProperty } from 'helpers/common.helper';

type ExporterProps = {
  data: any[];
  fileName: string;
  headerTable?: ITableField[];
  type?: ExportFileType;
  reportView?: ReportViewType;
  reportStructure?: ITableField[];
};

type Exporter = (exporterProps: ExporterProps) => void;

const ensurePdfFonts = (): void => {
  if (pdfmake.vfs) {
    return;
  }

  const globalObject = globalThis as any;
  if (!globalObject.pdfMake?.vfs) {
    // Execute pdfmake's UMD bundle with globalThis bound as `this`.
    // eslint-disable-next-line no-new-func
    const runVfsBundle = new Function(pdfFontsSource);
    runVfsBundle.call(globalObject);
  }

  pdfmake.vfs = globalObject.pdfMake?.vfs;
};

export const pdfReportViewReport = ({
  data,
  fileName,
  reportStructure,
}: ExporterProps): ITableInfo => {
  const { rowData, periods } = generateReportViewRowData(data, reportStructure);
  const pdfDataArr = [];
  const headers = [{ name: '' }].concat(
    periods.map((period) => ({
      name: period,
    })),
  );
  const MAX_CHARACTERS = 600;
  const columnSize = Math.floor(MAX_CHARACTERS / headers.length);
  const emptyColumns = Array(periods.length).fill('');

  reportStructure.map(({ name, key, type }) => {
    if (type === DataType.TOP_LEVEL_KEY) {
      pdfDataArr.push([wordWrap(name, columnSize), ...emptyColumns]);
    } else if (type === DataType.NESTED_OBJECT) {
      pdfDataArr.push([wordWrap(name, columnSize), ...emptyColumns]);

      Object.entries(rowData[key]).map(([subcat, value]) => {
        const subcatArr = [wordWrap(subcat, columnSize)];
        periods.map((period) => {
          const val = value[period] || '$0';
          subcatArr.push(wordWrap(val, columnSize));
        });
        pdfDataArr.push(subcatArr);
      });
    } else {
      const rowArr = [];
      rowArr.push(wordWrap(name, columnSize));
      periods.map((period) => {
        rowArr.push(wordWrap(rowData[key][period] || '$0', columnSize));
      });
      pdfDataArr.push(rowArr);
    }
  });

  const doc = pdfConfig(pdfDataArr, fileName, headers);
  return doc;
};

export const generateDownloadableReportView = (
  data: any[],
  json = false,
  reportStructure: any[],
): any[] => {
  const resp = [];
  if (data?.length > 0) {
    const { rowData, periods } = generateReportViewRowData(data, reportStructure);
    reportStructure.map(({ name, key, type }) => {
      if (type === DataType.TOP_LEVEL_KEY) {
        resp.push({ item: name });
      } else if (type === DataType.NESTED_OBJECT) {
        resp.push({ item: name });
        Object.entries(rowData[key]).map(([subcat, value]) => {
          const item = { item: subcat };
          periods.map((period) => {
            item[json ? camelCase(period) : period] = value[period] || '$0';
          });
          resp.push(item);
        });
      } else {
        const item = { item: name };
        periods.map((period) => {
          item[json ? camelCase(period) : period] = rowData[key][period] || '$0';
        });
        resp.push(item);
      }
    });
  }
  return resp;
};

export const generateDownloadableReport = (
  data: any[],
  headerTable = [],
  json = false,
  reportView = ReportViewType.LIST,
  reportStructure = [],
): any[] => {
  if (reportView === ReportViewType.REPORT)
    return generateDownloadableReportView(data, json, reportStructure);
  return data?.length > 0
    ? data.map((item) => {
        const result = {};
        headerTable.forEach(({ keys, name, type, key }) => {
          switch (key) {
            case TableHeaderKey.RATIO_VALUE:
              // eslint-disable-next-line no-case-declarations
              const nestedProperty = getNestedProperty(item, ['originalValue']);
              result[json ? camelCase(name) : name] = formatValue(
                json ? nestedProperty : String(nestedProperty),
                type,
              );
              break;
            default:
              result[json ? camelCase(name) : name] = formatValue(
                getNestedProperty(item, keys),
                type,
              );
          }
        });
        return result;
      })
    : [];
};

const saveDataToSheet: Exporter = ({
  data,
  fileName,
  headerTable,
  reportView,
  reportStructure,
  type,
}: ExporterProps) => {
  const splitFileName = fileName.split('_');
  const sheetName = splitFileName.length > 1 ? splitFileName[splitFileName.length - 1] : fileName;

  const sheet = xlsx.utils.json_to_sheet(
    generateDownloadableReport(data, headerTable, false, reportView, reportStructure),
  );
  const wb = xlsx.utils.book_new();
  xlsx.utils.book_append_sheet(wb, sheet, sheetName.slice(0, 31));
  xlsx.writeFile(wb, `${fileName}.${type}`);
};

const saveReportToPdf: Exporter = ({
  data,
  fileName,
  headerTable,
  reportView,
  reportStructure,
}) => {
  ensurePdfFonts();
  let doc = null;

  if (reportView === ReportViewType.REPORT)
    doc = pdfReportViewReport({ data, fileName, reportStructure });
  else {
    doc = pdfConfig(
      data.map((item) =>
        headerTable.map(({ keys, type, key }: any) => {
          const MAX_CHARACTERS = 200;
          const columnSize = Math.floor(MAX_CHARACTERS / headerTable.length);
          if (key === TableHeaderKey.RATIO_VALUE) {
            return wordWrap(
              formatValue(String(getNestedProperty(item, ['originalValue'])), type) || '',
              columnSize,
            );
          }
          return wordWrap(formatValue(getNestedProperty(item, keys), type) || '', columnSize);
        }),
      ),
      fileName,
      headerTable,
    );
  }
  pdfmake.createPdf(doc).download(fileName);
};

const saveReportToJson: Exporter = ({
  data,
  fileName,
  headerTable,
  reportView,
  reportStructure,
}) => {
  const fileToSave = new Blob(
    [
      JSON.stringify(
        generateDownloadableReport(data, headerTable, true, reportView, reportStructure),
      ),
    ],
    {
      type: 'application/json',
    },
  );
  FileSaver.saveAs(fileToSave, `${fileName}.json`);
};

const fileExporters: Record<ExportFileType, Exporter> = {
  pdf: saveReportToPdf,
  xlsx: saveDataToSheet,
  csv: saveDataToSheet,
  json: saveReportToJson,
};

export const exportFileFunction: Exporter = ({
  data,
  fileName,
  headerTable = [],
  type = ExportFileType.xlsx,
  reportView = ReportViewType.LIST,
  reportStructure,
}: ExporterProps): void => {
  const exporterFunction = fileExporters[type];
  exporterFunction({ data, fileName, headerTable, reportView, reportStructure, type });
};
